import { siteMetadata } from './site-metadata';

export type PageIconKind =
  | 'admin'
  | 'article'
  | 'auth'
  | 'bookmarks'
  | 'calendar'
  | 'coordination'
  | 'dashboard'
  | 'default'
  | 'forum'
  | 'groups'
  | 'home'
  | 'library'
  | 'newsletter'
  | 'notifications'
  | 'profile'
  | 'search'
  | 'settings'
  | 'share'
  | 'surveys'
  | 'writer';

export interface PageHeadDescriptor {
  title: string;
  iconKind: PageIconKind;
  iconHref: string;
  themeColor: string;
}

interface PageHeadRule {
  match: (pathname: string) => boolean;
  resolve: (pathname: string) => PageHeadResolved;
}

interface PageHeadResolved {
  label: PageHeadLabel;
  iconKind: PageIconKind;
}

interface PageHeadLabel {
  zh: string;
  en: string;
}

type PageHeadLocale = 'en' | 'zh';

interface IconTheme {
  bg: string;
  surface: string;
  accent: string;
  secondary: string;
  ink: string;
  glow: string;
  themeColor: string;
}

const DEFAULT_EN_SITE_NAME = 'Viquard';
const DEFAULT_ZH_SITE_NAME = '维启象限';

const ICON_THEMES: Record<PageIconKind, IconTheme> = {
  admin: iconTheme('#fbfaf7', '#fff1d8', '#9f5f16', '#2f6f6b', '#162423', '#f4c66d', '#9f5f16'),
  article: iconTheme('#f7fbfa', '#e0f3ef', '#245f66', '#ca8a2d', '#132c30', '#83d8cb', '#245f66'),
  auth: iconTheme('#f9fafb', '#e7eef9', '#315f72', '#c98d33', '#172338', '#95c4e6', '#315f72'),
  bookmarks: iconTheme('#fbfaf7', '#fff0dd', '#b96d22', '#315f72', '#2a1d12', '#f3bc72', '#b96d22'),
  calendar: iconTheme('#f8fbff', '#e8f1ff', '#315f72', '#d08d2d', '#13223a', '#91bde7', '#315f72'),
  coordination: iconTheme('#f7fbfa', '#e3f2ef', '#26736f', '#bb7a24', '#132f2e', '#83d5ca', '#26736f'),
  dashboard: iconTheme('#f8fbfa', '#e6f2ef', '#245f66', '#b97823', '#112a2e', '#86d2c6', '#245f66'),
  default: iconTheme('#f7fbfa', '#dff6f1', '#235f66', '#c78928', '#112b2f', '#7ad1c7', '#235f66'),
  forum: iconTheme('#faf9ff', '#ece9ff', '#4d5f9f', '#c7852f', '#171d39', '#b9b0ff', '#4d5f9f'),
  groups: iconTheme('#f8fbf8', '#e6f2de', '#3f6f43', '#c1842c', '#142414', '#acd49a', '#3f6f43'),
  home: iconTheme('#f7fbfa', '#e5f4ec', '#276b52', '#c7822a', '#12291e', '#9bd7ae', '#276b52'),
  library: iconTheme('#f8fbff', '#e6effa', '#315f72', '#d0922f', '#142637', '#9cc6e5', '#315f72'),
  newsletter: iconTheme('#fbfaf7', '#fff0db', '#9d6420', '#2e6b70', '#261d12', '#f3c477', '#9d6420'),
  notifications: iconTheme('#fffaf8', '#ffe9df', '#ad5d30', '#315f72', '#281912', '#f1aa81', '#ad5d30'),
  profile: iconTheme('#f8fbff', '#e8f1ff', '#315f72', '#b87926', '#132338', '#9bc3e5', '#315f72'),
  search: iconTheme('#f7fbfa', '#e2f2ef', '#246a66', '#b97824', '#122d2b', '#83d6ca', '#246a66'),
  settings: iconTheme('#f9fafb', '#e8edf3', '#53626f', '#be7d27', '#17202a', '#b4c1cc', '#53626f'),
  share: iconTheme('#f8fbff', '#e8f0fb', '#315f72', '#b97925', '#132438', '#9dc7e8', '#315f72'),
  surveys: iconTheme('#f7fbfa', '#e5f4ee', '#2e735f', '#bb7a24', '#122b23', '#9ed7be', '#2e735f'),
  writer: iconTheme('#fbfaf7', '#f7ead6', '#9b6725', '#245f66', '#251c11', '#e8ba71', '#9b6725'),
};

const DEFAULT_PAGE_LABEL = pageLabel('页面', 'Page');

const PAGE_HEAD_RULES: readonly PageHeadRule[] = [
  exact('/', pageLabel('首页', 'Home'), 'home'),
  exact('/dashboard', pageLabel('工作台', 'Dashboard'), 'dashboard'),
  exact('/admin', pageLabel('管理后台', 'Admin'), 'admin'),
  exact('/settings', pageLabel('设置', 'Settings'), 'settings'),
  exact('/profile', pageLabel('个人资料', 'Profile'), 'profile'),
  exact('/members', pageLabel('成员', 'Members'), 'profile'),
  exact('/notifications', pageLabel('通知', 'Notifications'), 'notifications'),
  exact('/search', pageLabel('搜索', 'Search'), 'search'),
  exact('/login', pageLabel('登录', 'Sign in'), 'auth'),
  exact('/register', pageLabel('创建账号', 'Create account'), 'auth'),
  exact('/reset-password', pageLabel('重置密码', 'Password reset'), 'auth'),
  exact('/verify-email', pageLabel('验证邮箱', 'Verify email'), 'auth'),
  exact('/bookmarks', pageLabel('收藏', 'Bookmarks'), 'bookmarks'),
  exact('/read-later', pageLabel('稍后阅读', 'Read later'), 'bookmarks'),
  exact('/calendar', pageLabel('日历', 'Calendar'), 'calendar'),
  exact('/calendar/coordination', pageLabel('协调', 'Coordination'), 'coordination'),
  exact('/calendar/scheduling', pageLabel('日程预约', 'Scheduling'), 'calendar'),
  exact('/scheduling', pageLabel('日程预约', 'Scheduling'), 'calendar'),
  exact('/parent', pageLabel('家长预约', 'Parent scheduling'), 'calendar'),
  exact('/groups', pageLabel('组织', 'Groups'), 'groups'),
  exact('/library', pageLabel('文件库', 'Library'), 'library'),
  exact('/library/downloads', pageLabel('下载记录', 'Downloads'), 'library'),
  exact('/surveys', pageLabel('问卷', 'Surveys'), 'surveys'),
  exact('/forum', pageLabel('论坛', 'Forum'), 'forum'),
  exact('/articles', pageLabel('文章', 'Articles'), 'article'),
  exact('/articles/new', pageLabel('写作', 'Write'), 'writer'),
  exact('/desk/newsletter', pageLabel('通讯编辑台', 'Newsletter desk'), 'newsletter'),
  prefix(
    '/articles/',
    (path) => (path.endsWith('/edit') ? pageLabel('编辑文章', 'Edit article') : pageLabel('文章', 'Article')),
    (path) => (path.endsWith('/edit') ? 'writer' : 'article'),
  ),
  prefix('/forum/new', pageLabel('新建讨论', 'New forum thread'), 'writer'),
  prefix('/forum/post/', pageLabel('论坛帖子', 'Forum post'), 'forum'),
  prefix('/forum/', pageLabel('论坛', 'Forum'), 'forum'),
  prefix('/surveys/new', pageLabel('新建问卷', 'New survey'), 'writer'),
  prefix(
    '/surveys/',
    (path) => (path.endsWith('/edit') ? pageLabel('编辑问卷', 'Edit survey') : pageLabel('问卷', 'Survey')),
    (path) => (path.endsWith('/edit') ? 'writer' : 'surveys'),
  ),
  prefix('/calendar/events/', pageLabel('日历事件', 'Calendar event'), 'calendar'),
  prefix('/groups/', pageLabel('组织', 'Group'), 'groups'),
  prefix('/invites/', pageLabel('组织邀请', 'Group invite'), 'groups'),
  prefix('/library/files/', pageLabel('共享文件', 'Shared file'), 'share'),
  prefix('/library/folders/', pageLabel('共享文件夹', 'Shared folder'), 'share'),
  prefix('/library/', pageLabel('文件夹', 'Folder'), 'library'),
  prefix('/s/', pageLabel('共享链接', 'Shared link'), 'share'),
  prefix('/users/', pageLabel('个人资料', 'Profile'), 'profile'),
  prefix('/categories/', pageLabel('分类', 'Category'), 'article'),
  prefix('/tags/', pageLabel('标签', 'Tag'), 'article'),
];

const iconHrefCache = new Map<PageIconKind, string>();

export function resolvePageHead(
  rawPathname: string,
  siteName: string = siteMetadata.name,
  language: string = readPageHeadLanguage(),
): PageHeadDescriptor {
  const pathname = normalisePathname(rawPathname);
  const rule = PAGE_HEAD_RULES.find((entry) => entry.match(pathname));
  const resolved = rule?.resolve(pathname) ?? {
    label: DEFAULT_PAGE_LABEL,
    iconKind: 'default',
  };
  const locale = resolvePageHeadLocale(language);
  const iconKind = resolved.iconKind;
  const cleanSiteName = cleanTitleSiteName(siteName, locale);
  return {
    title: formatPageTitle(resolved.label, cleanSiteName, locale),
    iconKind,
    iconHref: iconHrefForKind(iconKind),
    themeColor: ICON_THEMES[iconKind].themeColor,
  };
}

export function applyPageHead(head: PageHeadDescriptor): void {
  if (typeof document === 'undefined') return;
  document.title = head.title;
  const iconLink = ensureIconLink();
  if (iconLink.href !== head.iconHref) iconLink.href = head.iconHref;
  const themeMeta = ensureThemeColorMeta();
  if (themeMeta.content !== head.themeColor) themeMeta.content = head.themeColor;
}

export function iconHrefForKind(kind: PageIconKind): string {
  const cached = iconHrefCache.get(kind);
  if (cached !== undefined) return cached;
  const href = `data:image/svg+xml,${encodeURIComponent(renderIconSvg(kind))}`;
  iconHrefCache.set(kind, href);
  return href;
}

function iconTheme(
  bg: string,
  surface: string,
  accent: string,
  secondary: string,
  ink: string,
  glow: string,
  themeColor: string,
): IconTheme {
  return { bg, surface, accent, secondary, ink, glow, themeColor };
}

function exact(pathname: string, label: PageHeadLabel, iconKind: PageIconKind): PageHeadRule {
  return {
    match: (path) => path === pathname,
    resolve: () => ({ label, iconKind }),
  };
}

function prefix(
  pathname: string,
  label: PageHeadLabel | ((path: string) => PageHeadLabel),
  iconKind: PageIconKind | ((path: string) => PageIconKind),
): PageHeadRule {
  return {
    match: (path) => path.startsWith(pathname),
    resolve: (path) => ({
      label: typeof label === 'function' ? label(path) : label,
      iconKind: typeof iconKind === 'function' ? iconKind(path) : iconKind,
    }),
  };
}

function pageLabel(zh: string, en: string): PageHeadLabel {
  return { zh, en };
}

function formatPageTitle(label: PageHeadLabel, siteName: string, locale: PageHeadLocale): string {
  return locale === 'zh' ? `${label.zh}｜${siteName}` : `${label.en} | ${siteName}`;
}

function cleanTitleSiteName(siteName: string, locale: PageHeadLocale): string {
  if (locale === 'zh') return DEFAULT_ZH_SITE_NAME;
  const trimmed = siteName.trim();
  return trimmed.length > 0 && trimmed !== 'Personal Blog' ? trimmed : DEFAULT_EN_SITE_NAME;
}

function resolvePageHeadLocale(language: string): PageHeadLocale {
  return language.trim().toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

function readPageHeadLanguage(): string {
  if (typeof document !== 'undefined' && document.documentElement.lang.length > 0) {
    return document.documentElement.lang;
  }
  if (typeof navigator !== 'undefined') {
    const [primaryLanguage] = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
    if (typeof primaryLanguage === 'string') return primaryLanguage;
  }
  return 'en';
}

function normalisePathname(rawPathname: string): string {
  const rawPath = rawPathname.split(/[?#]/, 1)[0]?.trim() ?? '';
  const path = rawPath.length > 0 ? rawPath : '/';
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;
}

function ensureIconLink(): HTMLLinkElement {
  const selector = 'link[data-vq-route-icon="true"], link[rel~="icon"][type="image/svg+xml"]';
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  if (existing !== null) {
    existing.dataset.vqRouteIcon = 'true';
    existing.rel = 'icon';
    existing.type = 'image/svg+xml';
    return existing;
  }
  const link = document.createElement('link');
  link.dataset.vqRouteIcon = 'true';
  link.rel = 'icon';
  link.type = 'image/svg+xml';
  document.head.append(link);
  return link;
}

function ensureThemeColorMeta(): HTMLMetaElement {
  const existing = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (existing !== null) return existing;
  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  document.head.append(meta);
  return meta;
}

function renderIconSvg(kind: PageIconKind): string {
  const theme = ICON_THEMES[kind];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Viquard ${kind}">
<style>
.bg{fill:${theme.bg}}.surface{fill:${theme.surface}}.accent{fill:none;stroke:${theme.accent};stroke-width:4;stroke-linecap:round;stroke-linejoin:round}.accent-fill{fill:${theme.accent}}.secondary{fill:none;stroke:${theme.secondary};stroke-width:4;stroke-linecap:round;stroke-linejoin:round}.secondary-fill{fill:${theme.secondary}}.ink{fill:${theme.ink}}.glow{fill:${theme.glow};opacity:.72;transform-origin:32px 32px}
@media (prefers-color-scheme: dark){.bg{fill:#0f172a}.surface{fill:#172f34}.ink{fill:#edf7f5}}
@media (prefers-reduced-motion:no-preference){.glow{animation:vqPulse 4.6s ease-in-out infinite}.sweep{animation:vqSweep 7s linear infinite;transform-origin:32px 32px}@keyframes vqPulse{0%,100%{opacity:.42;transform:scale(.88)}50%{opacity:.9;transform:scale(1.05)}}@keyframes vqSweep{to{transform:rotate(360deg)}}}
</style>
<rect class="bg" width="64" height="64" rx="14"/>
${iconShape(kind)}
</svg>`;
}

function iconShape(kind: PageIconKind): string {
  switch (kind) {
    case 'admin':
      return '<path class="surface" d="M32 8 50 15v15c0 12-7 20-18 25-11-5-18-13-18-25V15l18-7Z"/><path class="accent" d="M32 8 50 15v15c0 12-7 20-18 25-11-5-18-13-18-25V15l18-7Z"/><path class="secondary" d="M24 31l6 6 12-15"/><circle class="glow" cx="45" cy="18" r="4"/>';
    case 'article':
      return '<path class="surface" d="M17 14h20c5 0 8 3 8 8v28H25c-5 0-8-3-8-8V14Z"/><path class="accent" d="M17 14h20c5 0 8 3 8 8v28H25c-5 0-8-3-8-8V14Z"/><path class="secondary" d="M25 24h12M25 32h15M25 40h10"/><circle class="glow" cx="47" cy="18" r="4"/>';
    case 'auth':
      return '<rect class="surface" x="16" y="27" width="32" height="23" rx="7"/><path class="accent" d="M16 36v-2c0-9 6-15 16-15s16 6 16 15v2"/><rect class="accent" x="16" y="27" width="32" height="23" rx="7"/><path class="secondary" d="M32 37v5"/><circle class="glow" cx="43" cy="20" r="4"/>';
    case 'bookmarks':
      return '<path class="surface" d="M21 12h22c2 0 4 2 4 4v36L32 44 17 52V16c0-2 2-4 4-4Z"/><path class="accent" d="M21 12h22c2 0 4 2 4 4v36L32 44 17 52V16c0-2 2-4 4-4Z"/><path class="secondary" d="M25 23h14M25 31h10"/><circle class="glow" cx="45" cy="16" r="4"/>';
    case 'calendar':
      return '<rect class="surface" x="13" y="17" width="38" height="34" rx="7"/><path class="accent" d="M20 12v9M44 12v9M13 27h38M13 17h38v34H13z"/><path class="secondary" d="M23 36h3M31 36h3M39 36h3M23 43h3M31 43h3"/><circle class="glow" cx="47" cy="14" r="4"/>';
    case 'coordination':
      return '<circle class="surface" cx="19" cy="21" r="7"/><circle class="surface" cx="45" cy="22" r="7"/><circle class="surface" cx="32" cy="44" r="8"/><path class="secondary" d="M25 24l14 14M39 25l-7 11"/><path class="accent" d="M19 21h.1M45 22h.1M32 44h.1"/><circle class="glow" cx="32" cy="44" r="5"/>';
    case 'dashboard':
      return '<path class="surface" d="M14 44c2-15 9-25 18-25s16 10 18 25H14Z"/><path class="accent" d="M14 44c2-15 9-25 18-25s16 10 18 25H14Z"/><path class="secondary" d="M32 44l10-16"/><path class="accent" d="M20 44h24"/><circle class="glow" cx="42" cy="28" r="4"/>';
    case 'forum':
      return '<path class="surface" d="M13 17h29c4 0 7 3 7 7v10c0 4-3 7-7 7H29l-11 8v-8h-5V17Z"/><path class="accent" d="M13 17h29c4 0 7 3 7 7v10c0 4-3 7-7 7H29l-11 8v-8h-5V17Z"/><path class="secondary" d="M23 27h16M23 34h10"/><circle class="glow" cx="47" cy="18" r="4"/>';
    case 'groups':
      return '<circle class="surface" cx="32" cy="24" r="9"/><path class="accent" d="M20 50c2-10 7-15 12-15s10 5 12 15"/><circle class="accent" cx="32" cy="24" r="9"/><path class="secondary" d="M16 44c2-7 5-11 10-12M48 44c-2-7-5-11-10-12"/><circle class="glow" cx="45" cy="21" r="4"/>';
    case 'home':
      return '<path class="surface" d="M13 31 32 15l19 16v20H18V31Z"/><path class="accent" d="M13 31 32 15l19 16v20H18V31Z"/><path class="secondary" d="M28 51V38h8v13M24 29c5-1 9-5 10-10"/><circle class="glow" cx="45" cy="28" r="4"/>';
    case 'library':
      return '<path class="surface" d="M12 21h18l5 6h17v20c0 3-2 5-5 5H17c-3 0-5-2-5-5V21Z"/><path class="accent" d="M12 21h18l5 6h17v20c0 3-2 5-5 5H17c-3 0-5-2-5-5V21Z"/><path class="secondary" d="M20 36h24M20 43h16"/><circle class="glow" cx="50" cy="28" r="4"/>';
    case 'newsletter':
      return '<rect class="surface" x="13" y="18" width="38" height="29" rx="7"/><path class="accent" d="M13 22l19 14 19-14M13 18h38v29H13z"/><path class="secondary" d="M22 39h20"/><circle class="glow" cx="48" cy="19" r="4"/>';
    case 'notifications':
      return '<path class="surface" d="M21 43V29c0-7 4-13 11-13s11 6 11 13v14l5 6H16l5-6Z"/><path class="accent" d="M21 43V29c0-7 4-13 11-13s11 6 11 13v14l5 6H16l5-6Z"/><path class="secondary" d="M27 51c1 3 3 5 5 5s4-2 5-5"/><circle class="glow" cx="43" cy="17" r="4"/>';
    case 'profile':
      return '<circle class="surface" cx="32" cy="24" r="10"/><circle class="accent" cx="32" cy="24" r="10"/><path class="secondary" d="M16 52c2-11 8-17 16-17s14 6 16 17"/><circle class="glow" cx="43" cy="18" r="4"/>';
    case 'search':
      return '<circle class="surface" cx="29" cy="28" r="14"/><circle class="accent" cx="29" cy="28" r="14"/><path class="secondary" d="M39 39l11 11M23 28h12"/><circle class="glow" cx="39" cy="18" r="4"/>';
    case 'settings':
      return '<circle class="surface" cx="32" cy="32" r="9"/><path class="accent sweep" d="M32 12v7M32 45v7M12 32h7M45 32h7M18 18l5 5M41 41l5 5M46 18l-5 5M23 41l-5 5"/><circle class="secondary" cx="32" cy="32" r="9"/><circle class="glow" cx="45" cy="19" r="4"/>';
    case 'share':
      return '<path class="surface" d="M20 20h16l8 8v24H20V20Z"/><path class="accent" d="M20 20h16l8 8v24H20V20Z"/><path class="secondary" d="M27 39c5-9 12-10 18-5M39 31h7v7"/><circle class="glow" cx="44" cy="27" r="4"/>';
    case 'surveys':
      return '<rect class="surface" x="16" y="12" width="34" height="42" rx="7"/><path class="accent" d="M16 12h34v42H16zM24 24l4 4 7-8M24 38l4 4 7-8"/><path class="secondary" d="M39 27h4M39 41h4"/><circle class="glow" cx="48" cy="15" r="4"/>';
    case 'writer':
      return '<path class="surface" d="M17 47 22 35 43 14l7 7-21 21-12 5Z"/><path class="accent" d="M17 47 22 35 43 14l7 7-21 21-12 5Z"/><path class="secondary" d="M38 19l7 7M22 35l7 7"/><circle class="glow" cx="48" cy="16" r="4"/>';
    case 'default':
      return '<path class="surface" d="M32 8 51 15v15c0 13-8 21-19 26-11-5-19-13-19-26V15l19-7Z"/><path class="accent" d="M32 8 51 15v15c0 13-8 21-19 26-11-5-19-13-19-26V15l19-7Z"/><path class="secondary" d="M20 22l11 22 14-26"/><circle class="glow" cx="45" cy="18" r="4"/>';
  }
}
